import type { MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Icon } from '../../components/Icon';
import {
  RAIL_ICON,
  RAIL_ITEM_IDLE_BG,
  RAIL_ITEM_IDLE_TEXT,
  railItem,
  railItemFill,
  railLabel,
  sheetItem,
} from '../../components/railItem';
import { trpc } from '../../lib/trpc';
import { workspaceHref } from './href';

function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    event.currentTarget.target !== '_blank'
  );
}

export function RailWorkspaces({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const query = useQuery(trpc.workspaces.list.queryOptions(undefined, { staleTime: 30_000 }));
  const pinned = query.data?.filter((workspace) => workspace.pinned) ?? [];
  if (pinned.length === 0) return null;

  return (
    <div className="mt-3 border-t border-rule pt-3 short:mt-2 short:pt-2">
      <p
        className={
          collapsed
            ? 'sr-only'
            : 'px-3 pb-1 text-[0.625rem] font-semibold tracking-[0.08em] text-ink-subtle uppercase'
        }
      >
        Workspaces
      </p>
      <div className="flex flex-col gap-1.5 short:gap-1">
        {pinned.map((workspace) => {
          const href = workspaceHref(workspace.route, workspace.id);
          return (
            <a
              key={workspace.id}
              href={href}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                void navigate({ href });
              }}
              title={collapsed ? workspace.name : undefined}
              className={`${railItem(collapsed)} ${RAIL_ITEM_IDLE_TEXT}`}
            >
              <span
                aria-hidden="true"
                className={`${railItemFill(collapsed)} ${RAIL_ITEM_IDLE_BG}`}
              />
              <Icon name="bookmark" size={RAIL_ICON} className="relative shrink-0" />
              <span className={railLabel(collapsed)}>{workspace.name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

export function MobileWorkspaceLinks({ onNavigate }: { onNavigate: () => void }) {
  const navigate = useNavigate();
  const query = useQuery(trpc.workspaces.list.queryOptions(undefined, { staleTime: 30_000 }));
  const pinned = query.data?.filter((workspace) => workspace.pinned) ?? [];
  if (pinned.length === 0) return null;

  return (
    <div className="mt-3 border-t border-rule pt-3">
      <p className="px-2 pb-1 text-[0.6875rem] font-semibold tracking-[0.08em] text-ink-subtle uppercase">
        Workspaces
      </p>
      <div className="flex flex-col gap-0.5">
        {pinned.map((workspace) => {
          const href = workspaceHref(workspace.route, workspace.id);
          return (
            <a
              key={workspace.id}
              href={href}
              onClick={(event) => {
                if (!isPlainClick(event)) return;
                event.preventDefault();
                onNavigate();
                void navigate({ href });
              }}
              className={sheetItem()}
            >
              <Icon name="bookmark" size={18} className="shrink-0 text-accent" />
              <span className="truncate">{workspace.name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
